/**
 * Service types ("trades") + price book — API client, shared cache, hooks.
 *
 * The workspace defines what it sells (Painting, Fences & Gates, Decks…) and a
 * price book of reusable line items. Deals, quotes and jobs carry a
 * `serviceCategory`; contact stage tags use its slug (`<slug>:state=<stage>`).
 *
 * Service types are read on almost every Work screen, so they live in one
 * module-level cache shared by every `useServiceCategories()` caller; writes
 * update the cache in place.
 */

import { useCallback, useEffect, useState, useSyncExternalStore } from 'react'
import { apiClient } from './api'
import { humanError } from './errors'

// ─── Types ────────────────────────────────────────────────────────────────────

/** The pastel token a service type stores (API field `color`). It is kept for
 *  API compatibility but no longer drawn: pastels mean phases of work
 *  (lib/phaseTone), so a lavender "Painting" dot would read as a lead. */
export type PastelColor = 'mint' | 'peach' | 'lavender' | 'butter' | 'blush' | 'sky'

export const PASTEL_COLORS: readonly PastelColor[] = ['lavender', 'butter', 'mint', 'peach', 'sky', 'blush']

/** MIDNIGHT pastel hexes (docs/DESIGN-SYSTEM.md) — the raw palette, keyed by
 *  token. Not for drawing a service type; use `pastelHex()` for that. */
export const PASTEL_HEX: Record<PastelColor, string> = {
  mint: 'var(--mint)',
  peach: 'var(--peach)',
  lavender: 'var(--lavender)',
  butter: 'var(--butter)',
  blush: 'var(--blush)',
  sky: 'var(--sky)',
}

/** Neutral glass dot used wherever a service type is drawn. */
export const SERVICE_TYPE_DOT = 'rgb(var(--fg-rgb) / 0.40)'

/**
 * Display color for a service type's dot. Service types are categories, not
 * phases of work, so every type renders neutral regardless of its stored
 * token (same rule as ServiceTypeChip). The argument stays so callers compile.
 */
export function pastelHex(_color: string | null | undefined): string {
  return SERVICE_TYPE_DOT
}

/** Compact shape embedded in deal / quote / job payloads. */
export interface ServiceCategoryBrief {
  id: string
  name: string
  slug: string
  color: string
}

export interface ServiceCategory extends ServiceCategoryBrief {
  description: string | null
  sortOrder: number
  isActive: boolean
  /** New price-book items of this type start taxable (F1). */
  taxable: boolean
}

export interface ServiceCategoryDraft {
  name: string
  color?: PastelColor
  description?: string | null
  isActive?: boolean
  taxable?: boolean
  sortOrder?: number
}

export interface CatalogItem {
  id: string
  name: string
  unit: string
  unitPrice: number
  defaultQuantity: number
  isOptional: boolean
  sku: string | null
  notes: string | null
  sortOrder: number
  isActive: boolean
  /** Sales tax applies when the workspace charges it (F1). */
  taxable: boolean
  isBuiltIn: boolean
  serviceCategoryId: string | null
  serviceCategory: ServiceCategoryBrief | null
}

export interface CatalogItemDraft {
  name: string
  unit?: string
  unitPrice: number
  defaultQuantity?: number
  isOptional?: boolean
  sku?: string | null
  notes?: string | null
  serviceCategoryId?: string | null
  isActive?: boolean
  sortOrder?: number
  /** Omit on create to inherit the service type's default. */
  taxable?: boolean
}

/** Stage-tag key for a record: its service slug, or "general" when it has none. */
export const GENERAL_SERVICE_SLUG = 'general'

export function serviceSlugOf(category: ServiceCategoryBrief | null | undefined): string {
  return category?.slug ?? GENERAL_SERVICE_SLUG
}

/** Human-readable message from an axios error. */
export function serviceCatalogErrorMessage(err: unknown, fallback: string): string {
  const e = err as { response?: { status?: number; data?: { error?: string; message?: string; details?: Array<{ message?: string }> } } }
  if (e?.response?.status === 409) return 'That name is already in use.'
  if (e?.response?.status === 403) return 'You do not have permission to change this.'
  return e?.response?.data?.details?.[0]?.message ?? e?.response?.data?.message ?? e?.response?.data?.error ?? fallback
}

// ─── Service categories: API ─────────────────────────────────────────────────

export async function listServiceCategories(includeInactive = true): Promise<ServiceCategory[]> {
  const res = await apiClient.get('/api/service-categories', {
    params: includeInactive ? { includeInactive: 'true' } : undefined,
  })
  return (res.data?.data?.items ?? []) as ServiceCategory[]
}

export async function createServiceCategory(draft: ServiceCategoryDraft): Promise<ServiceCategory> {
  const res = await apiClient.post('/api/service-categories', draft)
  return res.data?.data as ServiceCategory
}

export async function updateServiceCategory(id: string, draft: Partial<ServiceCategoryDraft>): Promise<ServiceCategory> {
  const res = await apiClient.patch(`/api/service-categories/${id}`, draft)
  return res.data?.data as ServiceCategory
}

export async function deleteServiceCategory(id: string): Promise<void> {
  await apiClient.delete(`/api/service-categories/${id}`)
}

export interface ServiceCategoryUsage {
  deals: number
  quotes: number
  jobs: number
  quoteTemplates: number
  catalogItems: number
}

/** How many records use a service type (shown before deleting it). */
export async function getServiceCategoryUsage(id: string): Promise<ServiceCategoryUsage> {
  const res = await apiClient.get(`/api/service-categories/${id}/usage`)
  return res.data?.data as ServiceCategoryUsage
}

/** "12 jobs, 4 quotes and 3 price-book items" — empty string when unused. */
export function describeServiceCategoryUsage(u: ServiceCategoryUsage): string {
  const parts: string[] = []
  const add = (n: number, one: string, many: string) => { if (n > 0) parts.push(`${n} ${n === 1 ? one : many}`) }
  add(u.deals, 'opportunity', 'opportunities')
  add(u.quotes, 'quote', 'quotes')
  add(u.jobs, 'job', 'jobs')
  add(u.quoteTemplates, 'quote template', 'quote templates')
  add(u.catalogItems, 'price-book item', 'price-book items')
  if (parts.length <= 1) return parts.join('')
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`
}

export async function restoreStarterServiceCategories(): Promise<ServiceCategory[]> {
  const res = await apiClient.post('/api/service-categories/restore-defaults')
  return (res.data?.data?.items ?? []) as ServiceCategory[]
}

// ─── Service categories: shared cache ────────────────────────────────────────

interface CategoryCacheState {
  items: ServiceCategory[]
  loaded: boolean
  loading: boolean
  error: string | null
  /** The failure itself, so screens can tell offline / no access / server apart. */
  rawError: unknown
}

let categoryState: CategoryCacheState = { items: [], loaded: false, loading: false, error: null, rawError: null }
let inflight: Promise<void> | null = null
const listeners = new Set<() => void>()

function setCategoryState(next: Partial<CategoryCacheState>) {
  categoryState = { ...categoryState, ...next }
  listeners.forEach((l) => l())
}

function sortCategories(items: ServiceCategory[]): ServiceCategory[] {
  return [...items].sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name))
}

export function loadServiceCategories(force = false): Promise<void> {
  if (inflight) return inflight
  if (categoryState.loaded && !force) return Promise.resolve()
  setCategoryState({ loading: true, error: null, rawError: null })
  inflight = listServiceCategories(true)
    .then((items) => setCategoryState({ items: sortCategories(items), loaded: true, loading: false }))
    .catch((err) =>
      setCategoryState({ loading: false, error: humanError(err, 'load service types'), rawError: err }),
    )
    .finally(() => {
      inflight = null
    })
  return inflight
}

/** Test/support hook: forget cached categories (e.g. after sign-out). */
export function resetServiceCategoryCache() {
  categoryState = { items: [], loaded: false, loading: false, error: null, rawError: null }
  inflight = null
  listeners.forEach((l) => l())
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export interface UseServiceCategories {
  /** Every category, active first-class; filter `isActive` for pickers. */
  categories: ServiceCategory[]
  /** Active categories only, in display order — what pickers should offer. */
  active: ServiceCategory[]
  loading: boolean
  /** One-line message ("Couldn't load service types. …") for inline text. */
  error: string | null
  /** The failure itself, for `<QueryState>` / `<ErrorState>` (offline vs 403 vs server). */
  rawError: unknown
  /** True once a load has succeeded (an empty list then really means "none set up"). */
  loaded: boolean
  byId: (id: string | null | undefined) => ServiceCategory | undefined
  bySlug: (slug: string | null | undefined) => ServiceCategory | undefined
  reload: () => Promise<void>
  create: (draft: ServiceCategoryDraft) => Promise<ServiceCategory>
  update: (id: string, draft: Partial<ServiceCategoryDraft>) => Promise<ServiceCategory>
  remove: (id: string) => Promise<void>
  restoreDefaults: () => Promise<void>
}

export function useServiceCategories(enabled = true): UseServiceCategories {
  const state = useSyncExternalStore(subscribe, () => categoryState, () => categoryState)

  useEffect(() => {
    if (enabled) void loadServiceCategories()
  }, [enabled])

  const byId = useCallback(
    (id: string | null | undefined) => (id ? state.items.find((c) => c.id === id) : undefined),
    [state.items],
  )
  const bySlug = useCallback(
    (slug: string | null | undefined) => (slug ? state.items.find((c) => c.slug === slug) : undefined),
    [state.items],
  )

  const create = useCallback(async (draft: ServiceCategoryDraft) => {
    const created = await createServiceCategory(draft)
    setCategoryState({ items: sortCategories([...categoryState.items, created]) })
    return created
  }, [])

  const update = useCallback(async (id: string, draft: Partial<ServiceCategoryDraft>) => {
    const updated = await updateServiceCategory(id, draft)
    setCategoryState({ items: sortCategories(categoryState.items.map((c) => (c.id === id ? updated : c))) })
    return updated
  }, [])

  const remove = useCallback(async (id: string) => {
    await deleteServiceCategory(id)
    setCategoryState({ items: categoryState.items.filter((c) => c.id !== id) })
  }, [])

  const restoreDefaults = useCallback(async () => {
    const items = await restoreStarterServiceCategories()
    setCategoryState({ items: sortCategories(items), loaded: true })
  }, [])

  return {
    categories: state.items,
    active: state.items.filter((c) => c.isActive),
    loading: state.loading || (!state.loaded && !state.error && enabled),
    error: state.error,
    rawError: state.rawError,
    loaded: state.loaded,
    byId,
    bySlug,
    reload: () => loadServiceCategories(true),
    create,
    update,
    remove,
    restoreDefaults,
  }
}

// ─── Price book: API ─────────────────────────────────────────────────────────

export async function listCatalogItems(opts: { includeInactive?: boolean } = {}): Promise<CatalogItem[]> {
  const res = await apiClient.get('/api/catalog-items', {
    params: opts.includeInactive ? { includeInactive: 'true' } : undefined,
  })
  return (res.data?.data?.items ?? []) as CatalogItem[]
}

export async function createCatalogItem(draft: CatalogItemDraft): Promise<CatalogItem> {
  const res = await apiClient.post('/api/catalog-items', draft)
  return res.data?.data as CatalogItem
}

export async function updateCatalogItem(id: string, draft: Partial<CatalogItemDraft>): Promise<CatalogItem> {
  const res = await apiClient.patch(`/api/catalog-items/${id}`, draft)
  return res.data?.data as CatalogItem
}

export async function deleteCatalogItem(id: string): Promise<void> {
  await apiClient.delete(`/api/catalog-items/${id}`)
}

export async function restoreStarterCatalogItems(): Promise<number> {
  const res = await apiClient.post('/api/catalog-items/restore-defaults')
  return Number(res.data?.data?.restored ?? 0)
}

export interface UseCatalogItems {
  items: CatalogItem[]
  loading: boolean
  /** One-line message ("Couldn't load the price book. …") for inline text. */
  error: string | null
  /** The failure itself, for `<QueryState>` / `<ErrorState>`. */
  rawError: unknown
  reload: () => Promise<void>
  create: (draft: CatalogItemDraft) => Promise<CatalogItem>
  update: (id: string, draft: Partial<CatalogItemDraft>) => Promise<CatalogItem>
  remove: (id: string) => Promise<void>
  restoreDefaults: () => Promise<number>
}

/** Loads the price book once per mount; `includeInactive` for the editor. */
export function useCatalogItems(opts: { enabled?: boolean; includeInactive?: boolean } = {}): UseCatalogItems {
  const enabled = opts.enabled ?? true
  const includeInactive = opts.includeInactive ?? false
  const [items, setItems] = useState<CatalogItem[]>([])
  const [loading, setLoading] = useState(enabled)
  const [rawError, setRawError] = useState<unknown>(null)
  const error = rawError ? humanError(rawError, 'load the price book') : null

  const reload = useCallback(async () => {
    setLoading(true)
    setRawError(null)
    try {
      setItems(await listCatalogItems({ includeInactive }))
    } catch (err) {
      setRawError(err)
    } finally {
      setLoading(false)
    }
  }, [includeInactive])

  useEffect(() => {
    if (!enabled) return
    let cancelled = false
    setLoading(true)
    listCatalogItems({ includeInactive })
      .then((rows) => { if (!cancelled) { setItems(rows); setRawError(null) } })
      .catch((err) => { if (!cancelled) setRawError(err) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [enabled, includeInactive])

  const create = useCallback(async (draft: CatalogItemDraft) => {
    const created = await createCatalogItem(draft)
    setItems((prev) => [...prev, created])
    return created
  }, [])

  const update = useCallback(async (id: string, draft: Partial<CatalogItemDraft>) => {
    const updated = await updateCatalogItem(id, draft)
    setItems((prev) => prev.map((i) => (i.id === id ? updated : i)))
    return updated
  }, [])

  const remove = useCallback(async (id: string) => {
    await deleteCatalogItem(id)
    setItems((prev) => prev.filter((i) => i.id !== id))
  }, [])

  const restoreDefaults = useCallback(async () => {
    const restored = await restoreStarterCatalogItems()
    await reload()
    return restored
  }, [reload])

  return { items, loading, error, rawError, reload, create, update, remove, restoreDefaults }
}
